'use client';

import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { motion, type TargetAndTransition, type Transition } from 'framer-motion';
import { ContainerPreset } from '@/constants/componentPresets';
import { ColorType } from '@/theme/colors';
import { Temper } from '@/types/frontend-types';
import { Ccontainer } from '../Ccontainer';
import { dataEl, type DataElScope } from '@/utils/dataEl';
import {
  MESSAGE_MODAL_GLOW_PULSE_DURATION_SECONDS,
  MESSAGE_MODAL_GLOW_PULSE_OPACITIES,
  MESSAGE_MODAL_GLOW_PULSE_TIMES,
  MESSAGE_MODAL_GLOW_REDUCED_MOTION_OPACITY,
  MESSAGE_MODAL_SURFACE_BORDER_OPACITY,
  MESSAGE_MODAL_SURFACE_GAP,
  MESSAGE_MODAL_SURFACE_RADIUS_PX,
} from './messageModalConstants';
import {
  getMessageModalGlowLayerStyle,
  getMessageModalOuterGlowStyle,
} from './messageModalStyles';

const LOADING_VALUE = 'loading';
const READY_VALUE = 'ready';
const LINEAR_EASING = 'linear';
const HIDDEN_OVERFLOW = 'hidden';
const REDUCED_MOTION_GLOW_ANIMATION: TargetAndTransition = {
  opacity: MESSAGE_MODAL_GLOW_REDUCED_MOTION_OPACITY,
};
const GLOW_PULSE_ANIMATION: TargetAndTransition = {
  opacity: MESSAGE_MODAL_GLOW_PULSE_OPACITIES,
};
const REDUCED_MOTION_GLOW_TRANSITION: Transition = { duration: 0 };
const GLOW_PULSE_TRANSITION: Transition = {
  duration: MESSAGE_MODAL_GLOW_PULSE_DURATION_SECONDS,
  ease: LINEAR_EASING,
  repeat: Infinity,
  times: MESSAGE_MODAL_GLOW_PULSE_TIMES,
};
const MESSAGE_MODAL_SURFACE_FRAME_STYLE: CSSProperties = {
  position: 'relative',
  width: '100%',
};

interface MessageModalSurfaceProps {
  dataElScope: DataElScope;
  loading: boolean;
  temper: Temper;
  reduceMotion: boolean;
  boxShadow: string;
  surfaceStyle: CSSProperties;
  height?: string;
  padding?: string;
  justifyContent?: 'center';
  children: ReactNode;
}

export function MessageModalSurface({
  dataElScope,
  loading,
  temper,
  reduceMotion,
  boxShadow,
  surfaceStyle,
  height,
  padding,
  justifyContent,
  children,
}: MessageModalSurfaceProps): ReactElement {
  const glowLayerStyle = getMessageModalGlowLayerStyle({ temper });
  const outerGlowStyle = getMessageModalOuterGlowStyle({ temper });
  const glowAnimation = reduceMotion
    ? REDUCED_MOTION_GLOW_ANIMATION
    : GLOW_PULSE_ANIMATION;
  const glowTransition = reduceMotion
    ? REDUCED_MOTION_GLOW_TRANSITION
    : GLOW_PULSE_TRANSITION;
  const surfaceFrameStyle: CSSProperties = {
    ...MESSAGE_MODAL_SURFACE_FRAME_STYLE,
    height,
  };

  return (
    <div
      data-el={dataEl(dataElScope, 'Surface/Frame')}
      style={surfaceFrameStyle}
    >
      <motion.div
        data-el={dataEl(dataElScope, 'Surface/OuterGlow')}
        aria-hidden="true"
        initial={false}
        animate={glowAnimation}
        transition={glowTransition}
        style={outerGlowStyle}
      />
      <Ccontainer
        preset={ContainerPreset.Default}
        backgroundColor={ColorType.Transparent}
        backgroundOpacity={0}
        backdropBlur={0}
        radius={MESSAGE_MODAL_SURFACE_RADIUS_PX}
        borderColor={ColorType.White}
        borderOpacity={MESSAGE_MODAL_SURFACE_BORDER_OPACITY}
        borderWidth={1}
        boxShadow={boxShadow}
        innerBorder={false}
        disableHoverShadow
        clickable={false}
        loading={loading}
        width="100%"
        height={height}
        padding={padding}
        flexDirection="column"
        alignItems="center"
        justifyContent={justifyContent}
        gap={MESSAGE_MODAL_SURFACE_GAP}
        overflow={HIDDEN_OVERFLOW}
        style={surfaceStyle}
        glowLayer={(
          <motion.div
            data-el={dataEl(dataElScope, 'Surface/GlowLayer')}
            aria-hidden="true"
            initial={false}
            animate={glowAnimation}
            transition={glowTransition}
            style={glowLayerStyle}
          />
        )}
      >
        {children}
      </Ccontainer>
    </div>
  );
}
